<template>
    <div class="gs-checkout-editor">
        <div class="gs-checkout-editor__intro">
            <div>
                <h4>Checkout form fields</h4>
                <p>Choose which fields appear on the checkout form. Required identity fields stay enabled to protect order records.</p>
            </div>
            <button type="button" class="gs-checkout-editor__primary" @click="openCustomFieldModal">
                <plus-icon class="h-4 w-4 fill-current"/>
                {{ $t('frontend_product_step5_add_custom') }}
            </button>
        </div>

        <div class="gs-checkout-field-grid">
            <article
                v-for="(formField, index) in localModel.checkout_page_options.form_fields"
                :key="index"
                class="gs-checkout-field-card"
                :class="{ 'is-disabled': !formField.enabled }"
            >
                <div class="gs-checkout-field-card__main">
                    <gd-checkbox
                        :variant="isLockedField(formField, index) ? 'base': 'primary'"
                        v-model="formField.enabled"
                        :disabled="isLockedField(formField, index)"
                    />
                    <div>
                        <strong>{{ formField.label }}</strong>
                        <span>{{ isLockedField(formField, index) ? 'Required system field' : fieldStateLabel(formField) }}</span>
                    </div>
                </div>
                <div class="gs-checkout-field-card__actions">
                    <label
                        v-if="canBeMandatory(formField)"
                        class="gs-checkout-field-card__toggle"
                        :class="{ 'is-muted': !formField.enabled }"
                    >
                        <gd-toggle :aria-label="`${formField.label || formField.type} ${$t('frontend_products_is_mandatory')}`" v-model="formField.mandatory" :value="formField.mandatory" :disable="!formField.enabled" />
                        <span>{{ $t('frontend_products_is_mandatory') }}</span>
                    </label>
                    <button
                        v-if="formField.type == 'custom'"
                        type="button"
                        class="gs-checkout-field-card__delete"
                        @click="openConfirmModal(index)"
                    >
                        <trash-icon class="h-4 w-4 fill-current"/>
                        Delete
                    </button>
                </div>
            </article>
        </div>
        <gs-alert-dialog
            v-model="showRemoveFormFieldConfirm"
            title="Remove custom field?"
            :copy="$t('frontend_non_revertable')"
            confirm-label="Delete"
            cancel-label="Cancel"
            @confirm="removeFormField"
        />
    </div>
</template>
<script>
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';

export default {
    components: {
        TrashIcon,
        PlusIcon
    },
    props: {
        model: {
            required: true
        },
        openCustomFieldModal: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            localModel: this.model,
            formFieldsIndex: null,
            showRemoveFormFieldConfirm: false,
        }
    },
    watch: {
        localModel: {
            handler() {
                this.$emit('updateModel', this.localModel);
            },
            deep: true
        }
    },
    methods: {
        isLockedField(formField, index) {
            return index < 4 || ['fname', 'lname', 'email', 'countryAndState'].includes(formField.type);
        },
        canBeMandatory(formField) {
            return ![
                'fname',
                'lname',
                'email',
                'countryAndState',
                'address',
                'cityAndPostalCode'
            ].includes(formField.type);
        },
        fieldStateLabel(formField) {
            if (!formField.enabled) return 'Hidden from checkout';
            if (formField.mandatory) return 'Shown and required';
            return 'Shown and optional';
        },
        openConfirmModal(index) {
            this.formFieldsIndex = index;
            this.showRemoveFormFieldConfirm = true;
        },
        async removeFormField() {
            this.localModel.checkout_page_options.form_fields.splice(this.formFieldsIndex, 1);
        },
    }
}
</script>
<style>
.gs-checkout-editor {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.gs-checkout-editor__intro {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 18px;
    background: var(--gs-surface-subtle);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
}

.gs-checkout-editor__intro h4 {
    margin: 0;
    color: var(--gs-foreground);
    font-size: 17px;
    font-weight: 600;
}

.gs-checkout-editor__intro p {
    margin: 6px 0 0;
    color: var(--gs-muted-foreground);
    font-size: 14px;
    line-height: 1.45;
}

.gs-checkout-editor__primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 14px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    background: var(--gs-primary);
    border: 1px solid var(--gs-primary);
    border-radius: var(--gs-radius-md);
}

.gs-checkout-field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.gs-checkout-field-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 118px;
    padding: 16px;
    background: #ffffff;
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
    box-shadow: var(--gs-shadow-sm);
}

.gs-checkout-field-card.is-disabled {
    background: var(--gs-surface-subtle);
}

.gs-checkout-field-card__main {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.gs-checkout-field-card__main strong {
    display: block;
    color: var(--gs-foreground);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.35;
}

.gs-checkout-field-card__main span,
.gs-checkout-field-card__toggle span {
    color: var(--gs-muted-foreground);
    font-size: 13px;
    line-height: 1.35;
}

.gs-checkout-field-card__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: auto;
}

.gs-checkout-field-card__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.gs-checkout-field-card__toggle.is-muted {
    opacity: .45;
}

.gs-checkout-field-card__delete {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--gs-primary);
    font-size: 13px;
    font-weight: 600;
    background: transparent;
    border: 0;
}

@media (max-width: 760px) {
    .gs-checkout-editor__intro,
    .gs-checkout-field-card__actions {
        flex-direction: column;
        align-items: stretch;
    }

    .gs-checkout-field-grid {
        grid-template-columns: 1fr;
    }
}
</style>
